<template>
  <div class="funnel-report bento-item" role="region" aria-label="Répartition des contenus par intention">
    <div class="head">
      <h3>Répartition de vos contenus</h3>
      <div class="period" role="group" aria-label="Période des contenus publiés">
        <button v-for="option in periods" :key="option.value" type="button" class="period-option" :class="{selected: period === option.value}" :aria-pressed="period === option.value" @click="period = option.value">{{ option.label }}</button>
      </div>
    </div>

    <div v-if="report" class="rows">
      <div v-for="row in rows" :key="row.label" class="row">
        <div class="row-label">
          <strong>{{ row.label }}</strong>
          <small>{{ row.sub }}</small>
        </div>
        <div v-if="row.total === undefined || row.total > 0" class="bar">
          <span v-for="segment in row.segments" :key="segment.stage.value" v-show="segment.percent > 0" class="segment" :style="{width: segment.percent + '%', backgroundColor: segment.stage.color, color: segment.stage.textColor}"
                :title="segment.title">{{ segment.percent >= 12 ? segment.percent + ' %' : '' }}</span>
        </div>
        <div v-else class="bar empty">{{ row.emptyText }}</div>
      </div>
    </div>
    <p v-else class="loading">{{ loadError ? "La répartition n'a pas pu être chargée." : "Chargement…" }}</p>

    <ul class="legend">
      <li v-for="stage in stages" :key="stage.value" :title="stage.help"><i class="dot" :style="{backgroundColor: stage.color}"></i>{{ stage.label }}</li>
    </ul>

    <p v-if="toClassify > 0" class="to-classify"><i class="dot"></i>{{ toClassifyText }}</p>
    <p v-if="advice" class="advice">{{ advice }}</p>
  </div>
</template>

<script>
import {StatsController} from "@/marketing/controllers/StatsController";
import {FUNNEL_STAGE_ENUM, suggestStage} from "@/marketing/utils";

const PERIODS = [
  {value: 'last3Months', label: '3 derniers mois'},
  {value: 'all', label: 'Depuis toujours'},
];

const plural = (count, singular, pluralForm) => `${count} ${count > 1 ? pluralForm : singular}`;

/**
 * Les trois barres « Visé / Publié / À venir » : la répartition des contenus entre les trois intentions.
 * Un simple comptage des intentions que vous avez choisies, aucune statistique n'y entre. load() recharge les chiffres.
 */
export default {
  name: 'FunnelReport',
  data() {
    return {
      report: undefined,
      loadError: false,
      period: 'last3Months',
      periods: PERIODS,
      stages: Object.values(FUNNEL_STAGE_ENUM),
    };
  },
  computed: {
    rows() {
      if (!this.report) return [];
      const published = this.report.published[this.period === 'all' ? 'all' : 'last3Months'];
      const periodText = this.period === 'all' ? 'depuis toujours' : 'sur 3 mois';
      return [
        {label: 'Visé', sub: 'votre objectif', segments: this.segments(this.report.objective, undefined)},
        {label: 'Publié', sub: `${plural(published.total, 'contenu', 'contenus')} ${periodText}`, total: published.total, emptyText: 'Aucun contenu publié sur cette période', segments: this.segments(published.percent, published.counts)},
        {label: 'À venir', sub: `${plural(this.report.upcoming.total, 'contenu', 'contenus')} prêt${this.report.upcoming.total > 1 ? 's' : ''} ou programmé${this.report.upcoming.total > 1 ? 's' : ''}`, total: this.report.upcoming.total, emptyText: 'Aucun contenu prêt ou programmé', segments: this.segments(this.report.upcoming.percent, this.report.upcoming.counts)},
      ];
    },
    // Contenus publiés sans intention (importés d'un réseau) : ils ne comptent pas dans les barres tant que vous ne les avez pas classés
    toClassify() {
      return this.report ? this.report.published[this.period === 'all' ? 'all' : 'last3Months'].unclassified ?? 0 : 0;
    },
    toClassifyText() {
      const period = this.period === 'all' ? 'Depuis toujours' : 'Sur 3 mois';
      return `${period}, ${plural(this.toClassify, 'publication importée est', 'publications importées sont')} à classer (filtre « À classer » de l'écran Contenus) : ${this.toClassify > 1 ? "elles n'entrent" : "elle n'entre"} pas dans la répartition ci-dessus.`;
    },
    // L'intention la plus en retard sur l'objectif, d'après les contenus publiés sur 3 mois et ceux à venir
    advice() {
      if (!this.report) return '';
      const planned = this.report.published.last3Months.total + this.report.upcoming.total;
      if (planned === 0) return '';
      const suggestion = suggestStage(this.report);
      if (!suggestion) return "Vos contenus récents et à venir tiennent l'objectif de répartition.";
      return `Pour tenir votre objectif, le prochain contenu devrait plutôt servir à « ${FUNNEL_STAGE_ENUM[suggestion.stage].label} » : ${suggestion.share} % des contenus récents et à venir, pour ${suggestion.objective} % visés.`;
    },
  },
  async mounted() {
    await this.load();
  },
  methods: {
    segments(percents, counts) {
      return this.stages.map(stage => ({
        stage,
        percent: percents[stage.key],
        title: counts ? `${stage.label} : ${percents[stage.key]} % (${plural(counts[stage.key], 'contenu', 'contenus')})` : `${stage.label} : ${percents[stage.key]} % visés`,
      }));
    },
    async load() {
      const report = await StatsController.funnel();
      this.loadError = !report || !!report.errors;
      this.report = this.loadError ? undefined : report;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.head
  display flex
  align-items center
  justify-content space-between
  flex-wrap wrap
  gap 10px
  margin-bottom 14px

  h3
    margin 0

// La période choisie est en bleu
.period
  display inline-flex
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  overflow hidden

.period-option
  height CTRL_HEIGHT_SM + 4px
  padding 0 14px
  border none
  border-right 1px solid CTRL_BORDER
  background white
  color FONT_COLOR
  font-family inherit
  font-size 13px
  font-weight 600
  cursor pointer

  &:last-child
    border-right none

  &:hover
    background CTRL_BG_HOVER

  &.selected
    background THEME_COLOR_PRIMARY
    color THEME_COLOR_PRIMARY_OPPOSITE

.rows
  display flex
  flex-direction column
  gap 12px

.row
  display grid
  grid-template-columns 170px 1fr
  align-items center
  gap 14px

.row-label
  display flex
  flex-direction column
  min-width 0

  strong
    color THEME_COLOR_PRIMARY

  small
    color CTRL_TEXT_MUTED
    font-size 0.8em

.bar
  display flex
  height 34px
  overflow hidden
  border-radius 10px
  border 1px solid CTRL_BORDER
  background white

  &.empty
    align-items center
    justify-content center
    border-style dashed
    color CTRL_TEXT_MUTED
    font-size 0.85em

.segment
  display flex
  align-items center
  justify-content center
  min-width 0
  overflow hidden
  font-size 13px
  font-weight 700
  white-space nowrap

.loading
  margin 0
  color CTRL_TEXT_MUTED

.legend
  display flex
  flex-wrap wrap
  gap 6px 20px
  margin 14px 0 0
  padding 0
  list-style none
  font-size 0.85em
  color FONT_COLOR

  li
    display inline-flex
    align-items center
    gap 6px

.dot
  width 10px
  height 10px
  border-radius 50%

// Contenus à classer : pastille vide, comme dans les tableaux
.to-classify
  display flex
  align-items center
  gap 8px
  margin 12px 0 0
  color CTRL_TEXT_MUTED
  font-size 0.88em

  .dot
    flex none
    box-sizing border-box
    border 2px solid CTRL_TEXT_MUTED
    background transparent

// Conseil : information neutre, en bleu
.advice
  margin 12px 0 0
  padding 10px 14px
  border-left 4px solid THEME_COLOR_PRIMARY
  border-radius 0 10px 10px 0
  background #eef2fb
  color FONT_COLOR
  font-size 0.92em

@media (max-width 700px)
  .row
    grid-template-columns 1fr
    gap 4px
</style>
